import store from '@/store'
import Vue from 'vue'
import VueRouter, { RouteConfig, Route, NavigationGuardNext } from 'vue-router'
import postAppMessage, { MessageHandlers } from '@/utils/poster'
import { getAndSetHostIdFromLocalStorage, getFacebookParamsFromUrl } from '@/utils'
import { CartType } from '@/store/types'

import HomeView from '@/views/Home.vue'
import LoginView from '@/views/Login.vue'
import SignUpView from '@/views/SignUp.vue'
import ProductView from '@/views/Product.vue'
import CartView from '@/views/Cart.vue'
import CheckoutView from '@/views/Checkout.vue'
import PaymentView from '@/views/Payment.vue'
import OrderView from '@/views/Order.vue'
import OrderDetailsView from '@/views/OrderDetails.vue'
import UserInfoView from '@/views/UserInfo.vue'
import PersonalBookView from '@/views/PersonalBook/PersonalBook.vue'
import PersonalBookCoverView from '@/views/PersonalBook/PersonalBookCover.vue'
import PersonalBookBackgroundView from '@/views/PersonalBook/PersonalBookBackground.vue'
import BookPreviewView from '@/views/PersonalBook/BookPreview.vue'
import FigurePaintingView from '@/views/FigurePainting/index.vue'
import FigurePaintingFormView from '@/views/FigurePainting/FigurePaintingForm.vue'
import FigurePaintingSummaryView from '@/views/FigurePainting/FigurePaintingSummary.vue'
import FigurePaintingImageView from '@/views/FigurePainting/FigurePaintingImage.vue'
import FigurePaintingSubmitView from '@/views/FigurePainting/FigurePaintingSubmit.vue'
import FigurePaintingResultView from '@/views/FigurePainting/FigurePaintingResult.vue'

Vue.use(VueRouter)

const routes: Array<RouteConfig> = [
  {
    path: '/',
    name: 'Home',
    component: HomeView,
    beforeEnter: (to, from, next) => {
      store.commit('info/RESET_ROUTE_HISTORY')
      next()
    }
  },
  {
    path: '/login',
    name: 'Login',
    props: route => ({ redirect: route.query.redirect }),
    component: LoginView,
    beforeEnter: (to, from, next) => {
      if (store.getters.isLogin) {
        next({ path: from.query.redirect as string || '/' })
      }
      next()
    }
  },
  {
    path: '/signup',
    alias: ['/recovery', '/user/password'],
    name: 'SignUp',
    props: route => ({ ...route.params, redirect: route.query.redirect }),
    component: SignUpView,
    beforeEnter: (to, from, next) => {
      if (store.getters.isLogin) {
        next({ path: from.query.redirect as string || '/' })
      }
      next()
    }
  },
  {
    path: '/product/:productId',
    name: 'Product',
    component: ProductView,
    beforeEnter: (to, from, next) => {
      next()
    }
  },
  {
    path: '/cart/',
    name: 'Cart',
    component: CartView,
    beforeEnter: (to, from, next) => {
      if (to.query.webview === '1') {
        next()
      } else {
        redirectIfNotLogin(to, from, next)
      }
    }
  },
  {
    path: '/checkout/',
    name: 'Checkout',
    component: CheckoutView,
    beforeEnter: async (to, from, next) => {
      if (store.getters.isLogin || to.query.webview === '1') {
        try {
          // type = 1, get checkout cart list
          await store.dispatch('GET_CART_LIST', CartType.checkout)

          // If no checkout item, back to cart
          if (store.getters.cartCheckedCount === 0) {
            next({
              name: 'Cart',
              query: to.query
            })
          } else {
            if (store.getters.orderInfo.success === 1) {
              next()
            } else {
              next({
                name: 'Cart',
                query: to.query
              })
            }
          }
        } catch (error) {
          store.commit('logger/PUSH_LOG', error)
          // https://router.vuejs.org/guide/advanced/navigation-failures.html#detecting-navigation-failures
          next(error)
        }
      } else {
        next({
          name: 'Login',
          query: { redirect: to.fullPath }
        })
      }
    }
  },
  {
    path: '/payment/:orderId',
    name: 'Payment',
    props: true,
    component: PaymentView,
    beforeEnter: (to, from, next) => {
      if (to.query.webview === '1') {
        next()
      } else {
        redirectIfNotLogin(to, from, next)
      }
    }
  },
  {
    path: '/order',
    name: 'Order',
    props: true,
    component: OrderView,
    beforeEnter: (to, from, next) => {
      if (to.query.webview === '1') {
        next()
      } else {
        redirectIfNotLogin(to, from, next)
      }
    }
  },
  {
    path: '/order/:orderId',
    name: 'OrderDetails',
    props: true,
    component: OrderDetailsView,
    beforeEnter: (to, from, next) => {
      if (to.query.webview === '1') {
        next()
      } else {
        redirectIfNotLogin(to, from, next)
      }
    }
  },
  {
    path: '/user',
    name: 'UserInfo',
    props: true,
    component: UserInfoView,
    beforeEnter: (to, from, next) => {
      if (to.query.webview === '1') {
        next()
      } else {
        redirectIfNotLogin(to, from, next)
      }
    }
  },
  {
    path: '/personalbook',
    redirect: '/product100'
  },
  {
    path: '/product100',
    name: 'PersonalBook',
    props: true,
    component: PersonalBookView,
    beforeEnter: (to, from, next) => {
      if (to.query.webview === '1') {
        next()
      } else {
        redirectIfNotLogin(to, from, next)
      }
    }
  },
  {
    path: '/product100/cover',
    name: 'PersonalBookCover',
    props: true,
    component: PersonalBookCoverView,
    beforeEnter: (to, from, next) => {
      if (to.query.webview === '1') {
        next()
      } else {
        redirectIfNotLogin(to, from, next)
      }
    }
  },
  {
    path: '/product100/background',
    name: 'PersonalBook2',
    props: true,
    component: PersonalBookBackgroundView,
    beforeEnter: (to, from, next) => {
      if (to.query.webview === '1') {
        next()
      } else {
        redirectIfNotLogin(to, from, next)
      }
    }
  },
  {
    path: '/product100/preview',
    name: 'BookPreview',
    props: true,
    component: BookPreviewView,
    beforeEnter: (to, from, next) => {
      if (to.query.webview === '1') {
        next()
      } else {
        if (store.getters.isLogin) {
          next()
        } else {
          next({
            name: 'Login',
            query: { redirect: '/product100' }
            // https://github.com/vuejs/vue-router/issues/2833#issuecomment-532158403
            // Remember to handle error when using $router.push since navigation failure makes reject promise
          })
        }
      }
    }
  },
  {
    path: '/product120',
    name: 'FigurePaintingView',
    props: true,
    component: FigurePaintingView,
    children: [
      {
        path: 'form',
        name: 'FigurePaintingFormView',
        component: FigurePaintingFormView
      },
      {
        path: 'summary',
        name: 'FigurePaintingSummaryView',
        component: FigurePaintingSummaryView
      },
      {
        path: 'image',
        name: 'FigurePaintingImageView',
        component: FigurePaintingImageView
      },
      {
        path: 'submit',
        name: 'FigurePaintingSubmitView',
        component: FigurePaintingSubmitView
      },
      {
        path: 'result',
        name: 'FigurePaintingResultView',
        component: FigurePaintingResultView
      }
    ],
    beforeEnter: (to, from, next) => {
      if (to.query.webview === '1') {
        next()
      } else {
        if (store.getters.isLogin) {
          next()
        } else {
          next({
            name: 'Login',
            query: { redirect: to.fullPath }
            // https://github.com/vuejs/vue-router/issues/2833#issuecomment-532158403
            // Remember to handle error when using $router.push since navigation failure makes reject promise
          })
        }
      }
    }
  },
  { path: '*', component: () => import('@/views/Home.vue') }
]

function redirectIfNotLogin (to: Route, from: Route, next: NavigationGuardNext) {
  if (store.getters.isLogin) {
    next()
  } else {
    next({
      name: 'Login',
      query: { redirect: to.fullPath }
      // https://github.com/vuejs/vue-router/issues/2833#issuecomment-532158403
      // Remember to handle error when using $router.push since navigation failure makes reject promise
    })
  }
}

const router = new VueRouter({
  base: process.env.BASE_URL,
  mode: 'history',
  routes,
  scrollBehavior (to, from, savedPosition) {
    // UX improvement
    if (from.name === to.name && to.name === 'Home') return
    if (savedPosition) {
      return savedPosition
    } else {
      return new Promise((resolve) => {
        resolve({ x: 0, y: 0 })
      })
    }
  }
})

router.onError((error) => {
  console.log(error)
  store.commit('logger/PUSH_LOG', 'route error')
  store.commit('logger/PUSH_LOG', error)
})

router.beforeEach((to, from, next) => {
  store.commit('SET_ROUTES', { from, to })
  store.commit('logger/PUSH_LOG', `${from.name} -> ${to.name}`)

  // Keep queries in routes
  if (from.query.webview !== undefined) {
    to.query.webview = from.query.webview
  }
  if (from.query.token !== undefined) {
    to.query.token = from.query.token
  }

  // Redirect from Facebook Login
  if (to.name === 'Login' && (to.query.code === undefined)) {
    console.log(window.location.href)
    const query = getFacebookParamsFromUrl(window.location.href)
    if (query.code && query.state) {
      const stateStr = query.state as string
      const state = JSON.parse(stateStr)

      // HostId has been modified by the middle man
      if (state.hostId !== getAndSetHostIdFromLocalStorage()) {
        store.commit('logger/PUSH_LOG', `裝置的版本與Facebook回傳不一致：${state.hostId}, ${getAndSetHostIdFromLocalStorage()}`)
        next({
          name: 'Login',
          query: {
            error: 'access_denied',
            errorDescription: '裝置的版本與Facebook回傳不一致，請聯絡我們的客服協助您處理此問題。'
          }
        })
        return
      }

      if (state.redirect) {
        query.redirect = state.redirect
      }

      next({
        name: 'Login',
        query
      })
      return
    }
  }

  // Send set title
  if (to.query.webview === '1' && to.name !== 'PersonalBookCover') {
    postAppMessage(MessageHandlers.UPDATE_ROUTE, to.name)
  }

  // Redirect if fullPath begins with a hash (ignore hashes later in path)
  if (to.fullPath.substr(0, 2) === '/#') {
    const path = to.fullPath.substr(2)
    const query = to.query
    next({ path: path, query: query })
    return
  }

  next()
})

export default router
